import { getUserWithInternalAccess } from "@/api/user/get-user-internal-access-token";
import { getUserPermissionsWithInternalAccess } from "@/api/user/user-permissions";
import httpRequest from "@/auth/http-client";
import { getAccessToken } from "@/helpers/auth-helpers";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import IUser, { IUserInput } from "@/interfaces/user.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

export interface User {
  id: number;
  userKey: string;
  status: string;
  accessCount: number;
  email: string;
  enableSoundNotification: boolean;
  languageCode: string;
  lastAccess: string;
  name: string;
  phone1: string;
  phone2: string;
  rowdate: string;
  userProfile: Array<{
    userId: number;
    profileId: number;
    rowDate: string;
  }>;
  permissions?: UserPermission[];
}

export interface UserPermission {
  id: number;
  code: string;
  label: string;
  endpoint: string;
  restricted: number;
  rowdate?: string;
}

class UserService {
  private baseUrl = "api/v1/user";

  async filter(
    filter: IFilter<IUser>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IUser>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.post(`${this.baseUrl}/filter?${paginationQuery}`, {
      filter,
    });

    return response;
  }

  async getById(userId?: number): Promise<User> {
    try {
      const accessToken = getAccessToken();
      const customerId = this.getCustomerId();
      const currentUserId = userId || this.getUserId();

      if (!accessToken || !customerId || !currentUserId) {
        throw new Error("Missing authentication data");
      }

      const userData = await getUserWithInternalAccess({
        customerId,
        userId: currentUserId,
      });

      const permissions = await this.getUserPermissions();

      return {
        ...userData,
        permissions,
      };
    } catch (error) {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error("Access token not found");
      }

      const tokenData = this.decodeToken(accessToken);
      if (!tokenData) {
        throw new Error("Invalid token");
      }

      return {
        id: tokenData.userId,
        userKey: "",
        status: "A",
        accessCount: 0,
        email: tokenData.email,
        enableSoundNotification: true,
        languageCode: "PT-BR",
        lastAccess: new Date().toISOString(),
        name: tokenData.email,
        phone1: "",
        phone2: "",
        rowdate: new Date().toISOString(),
        userProfile: [
          {
            userId: tokenData.userId,
            profileId: 1,
            rowDate: new Date().toISOString(),
          },
        ],
        permissions: [],
      };
    }
  }

  async getUserPermissions(): Promise<UserPermission[]> {
    try {
      const accessToken = getAccessToken();
      const customerId = this.getCustomerId();
      const userId = this.getUserId();

      if (!accessToken || !customerId || !userId) {
        throw new Error("Missing authentication data");
      }

      const permissions = await getUserPermissionsWithInternalAccess({
        customerId,
        userId,
      });

      return permissions;
    } catch (error) {
      return [];
    }
  }

  private getCustomerId(): string | null {
    const accessToken = getAccessToken();
    if (!accessToken) return null;

    const tokenData = this.decodeToken(accessToken);
    return tokenData?.customerId || null;
  }

  private getUserId(): number | null {
    const accessToken = getAccessToken();
    if (!accessToken) return null;

    const tokenData = this.decodeToken(accessToken);
    return tokenData?.userId || null;
  }

  private decodeToken(token: string) {
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return {
        userId: decoded.UserID,
        email: decoded.sub,
        customerId: decoded.CustomerId,
      };
    } catch (error) {
      return null;
    }
  }

  async update(userId: number, formData: Record<string, unknown>): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${userId}`, formData);
  }

  async delete(userId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${userId}`);
  }

  async create(formData: IUserInput): Promise<IUser> {
    return await httpRequest.post(`${this.baseUrl}`, formData);
  }
}

export const userService = new UserService();
